import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { userAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import './Team.css';

const TeamMemberDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    loadMemberDetails();
  }, [id]);

  const loadMemberDetails = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getById(id);
      setMember(response.data.data);
    } catch (error) {
      console.error('Failed to load member details:', error);
      alert('Failed to load team member details');
      navigate('/team');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to deactivate ${member.name}?`)) return;
    try {
      await userAPI.delete(id);
      alert('Team member deactivated successfully');
      navigate('/team');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to deactivate team member');
    }
  };

  const canManageTeam = user?.role === 'HR' || user?.role === 'Operations Head';

  if (loading) {
    return (
      <Layout>
        <div className="team-page">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading member details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!member) {
    return (
      <Layout>
        <div className="team-page">
          <div className="empty-state">
            <h3>Team member not found</h3>
            <button className="btn-create" onClick={() => navigate('/team')}>
              Back to Team
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="team-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Team Member Details</h1>
            <p className="page-subtitle">{member.name}</p>
          </div>
          <button className="btn-cancel" onClick={() => navigate('/team')}>
            ← Back to Team
          </button>
        </div>

        <div className="details-page-content">
          <div className="details-header-enhanced">
            <div className="avatar-wrapper">
              <Avatar 
                name={member.name} 
                profileImage={member.profileImage} 
                size="large" 
                className="details-avatar"
              />
            </div>
            <div className="member-header-info">
              <h2 className="member-name-large">{member.name}</h2>
              <div className="member-meta">
                <span className="member-role-badge">{member.role}</span>
                <span className={`member-status-badge ${member.isActive ? 'active' : 'inactive'}`}>
                  <span className="status-dot"></span>
                  {member.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="details-section-enhanced">
            <h3 className="section-title-enhanced">
              <svg className="section-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Personal Information
            </h3>
            <div className="details-grid-enhanced">
              <div className="detail-item-enhanced">
                <span className="detail-label-enhanced">Email</span>
                <span className="detail-value-enhanced">{member.email}</span>
              </div>
              <div className="detail-item-enhanced">
                <span className="detail-label-enhanced">Mobile</span>
                <span className="detail-value-enhanced">{member.mobile || 'Not provided'}</span>
              </div>
              {member.dateOfBirth && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Date of Birth</span>
                  <span className="detail-value-enhanced">{new Date(member.dateOfBirth).toLocaleDateString()}</span>
                </div>
              )}
              {member.gender && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Gender</span>
                  <span className="detail-value-enhanced">{member.gender}</span>
                </div>
              )}
              {member.maritalStatus && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Marital Status</span>
                  <span className="detail-value-enhanced">{member.maritalStatus}</span>
                </div>
              )}
              {member.nationality && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Nationality</span>
                  <span className="detail-value-enhanced">{member.nationality}</span>
                </div>
              )}
            </div>
          </div>

          {(member.address || member.city || member.state || member.zipCode || member.country) && (
            <div className="details-section-enhanced">
              <h3 className="section-title-enhanced">
                <svg className="section-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                Address Details
              </h3>
              <div className="details-grid-enhanced">
                {member.address && (
                  <div className="detail-item-enhanced full-width">
                    <span className="detail-label-enhanced">Street Address</span>
                    <span className="detail-value-enhanced">{member.address}</span>
                  </div>
                )}
                {member.city && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">City</span>
                    <span className="detail-value-enhanced">{member.city}</span>
                  </div>
                )}
                {member.state && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">State/Province</span>
                    <span className="detail-value-enhanced">{member.state}</span>
                  </div>
                )}
                {member.zipCode && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Zip Code</span>
                    <span className="detail-value-enhanced">{member.zipCode}</span>
                  </div>
                )}
                {member.country && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Country</span>
                    <span className="detail-value-enhanced">{member.country}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {(member.emergencyContactName || member.emergencyContactPhone || member.emergencyContactRelation) && (
            <div className="details-section-enhanced">
              <h3 className="section-title-enhanced">
                <svg className="section-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Emergency Contact
              </h3>
              <div className="details-grid-enhanced">
                {member.emergencyContactName && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Contact Name</span>
                    <span className="detail-value-enhanced">{member.emergencyContactName}</span>
                  </div>
                )}
                {member.emergencyContactPhone && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Contact Phone</span>
                    <span className="detail-value-enhanced">{member.emergencyContactPhone}</span>
                  </div>
                )}
                {member.emergencyContactRelation && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Relationship</span>
                    <span className="detail-value-enhanced">{member.emergencyContactRelation}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="details-section-enhanced">
            <h3 className="section-title-enhanced">
              <svg className="section-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              Employment Details
            </h3>
            <div className="details-grid-enhanced">
              {member.department && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Department</span>
                  <span className="detail-value-enhanced">{member.department}</span>
                </div>
              )}
              {member.employeeId && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Employee ID</span>
                  <span className="detail-value-enhanced">{member.employeeId}</span>
                </div>
              )}
              {member.designation && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Designation</span>
                  <span className="detail-value-enhanced">{member.designation}</span>
                </div>
              )}
              {member.joiningDate && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Joining Date</span>
                  <span className="detail-value-enhanced">{new Date(member.joiningDate).toLocaleDateString()}</span>
                </div>
              )}
              {member.employmentType && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Employment Type</span>
                  <span className="detail-value-enhanced">{member.employmentType}</span>
                </div>
              )}
              {member.workLocation && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Work Location</span>
                  <span className="detail-value-enhanced">{member.workLocation}</span>
                </div>
              )}
              {member.salary && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Annual Salary</span>
                  <span className="detail-value-enhanced salary-value">${member.salary.toLocaleString()}</span>
                </div>
              )}
              {member.reportsTo && (
                <div className="detail-item-enhanced">
                  <span className="detail-label-enhanced">Reports To</span>
                  <span className="detail-value-enhanced">{member.reportsTo.name}</span>
                </div>
              )}
            </div>
          </div>

          {(member.education || member.university || member.graduationYear || member.experience || member.previousCompany || member.skills) && (
            <div className="details-section-enhanced">
              <h3 className="section-title-enhanced">
                <svg className="section-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
                Education & Experience
              </h3>
              <div className="details-grid-enhanced">
                {member.education && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Highest Education</span>
                    <span className="detail-value-enhanced">{member.education}</span>
                  </div>
                )}
                {member.university && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">University</span>
                    <span className="detail-value-enhanced">{member.university}</span>
                  </div>
                )}
                {member.graduationYear && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Graduation Year</span>
                    <span className="detail-value-enhanced">{member.graduationYear}</span>
                  </div>
                )}
                {member.experience && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Experience</span>
                    <span className="detail-value-enhanced">{member.experience}</span>
                  </div>
                )}
                {member.previousCompany && (
                  <div className="detail-item-enhanced">
                    <span className="detail-label-enhanced">Previous Company</span>
                    <span className="detail-value-enhanced">{member.previousCompany}</span>
                  </div>
                )}
                {member.skills && (
                  <div className="detail-item-enhanced full-width">
                    <span className="detail-label-enhanced">Skills</span>
                    <span className="detail-value-enhanced">{member.skills}</span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default TeamMemberDetails;
